import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ApiService } from '../../shared/services/api.service';
import { Institution, InstitutionService } from './institution.service';

export interface Group {
  groupId: string;
  name: string;
  dateCreated: Date;
  groupType: number;
  sort: number;
  description: string;
  criteria: string;
  groupCode: string;
  groupCodeDescription: string;
  institutionCount: number;
  displayDescription: string;
  isOpen: boolean;
  sortColumn: string;
  sortDir: string;
  page: number;
  iids: number[];
  institutions: GroupInstitution[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  institutionProfiles: any[];
}

export interface GroupInstitution extends Institution {
  isSelected: boolean;
}

export interface CreateGroup {
  name: string;
  description: string;
  criteria: string;
  iids: number[];
}

export interface UpdateGroup {
  groupId: string;
  name: string;
  description: string;
  institutions: UpdateGroupInstitution[];
}

export interface UpdateGroupInstitution {
  iid: number;
  isAdded: boolean;
  isRemoved: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  private apiService = inject(ApiService);
  private institutionService = inject(InstitutionService);
  
  constructor() { }

  public getGroups(iid: number): Observable<Group[]> {
    return this.apiService.get<Group[]>(`/api/${iid}/group`)
      .pipe(map(groups => groups.map(group =>
      ({
        ...group,
        institutions: group.iids.map(iid =>
        ({
          ...this.institutionService.getInstitution(iid) ?? {} as GroupInstitution,
          isSelected: true,
          isAdded: false,
          isRemoved: false
        })),
        institutionProfiles: []
      }))));
  }

  public getGroupInstitutions(iid: number, groupId: string): Observable<Institution[]> {
    return this.apiService.get<Institution[]>(`/api/${iid}/group/${groupId}/institutions`);
  }

  public createGroup(iid: number, group: Group): Observable<Group[]> {
    return this.apiService.post<Group[]>(`/api/${iid}/group`, group)
      .pipe(map(groups => groups.map(group =>
      ({
        ...group,
        institutions: group.iids.map(iid =>
        ({
          ...this.institutionService.getInstitution(iid) ?? {} as GroupInstitution,
          isSelected: true,
          isAdded: false,
          isRemoved: false
        })),
        institutionProfiles: []
      }))));
  }

  public updateGroup(iid: number, updateGroups: UpdateGroup[]): Observable<Group[]> {
    return this.apiService.put<Group[]>(`/api/${iid}/group`, updateGroups)
      .pipe(map(groups => groups.map(group =>
      ({
        ...group,
        institutions: group.iids.map(iid =>
        ({
          ...this.institutionService.getInstitution(iid) ?? {} as GroupInstitution,
          isSelected: true,
          isAdded: false,
          isRemoved: false
        })),
        institutionProfiles: []
      }))));
  }

  public deleteGroup(iid: number, groupId: string) {
    return this.apiService.delete(`api/${iid}/group/${groupId}`);
  }
}
